/**
 * Token Refresh Retry Guard
 * 
 * Prevents retry storms and implements circuit breaker pattern for token refresh operations.
 * Uses exponential backoff and tracks failure counts to avoid overwhelming the auth endpoint.
 * 
 * Security Note: This guards against both accidental retry loops and potential DoS scenarios.
 */

interface RetryState {
  failureCount: number;
  lastFailureTime: number | null;
  circuitOpen: boolean;
  circuitOpenTime: number | null;
}

class TokenRetryGuard {
  private state: RetryState = {
    failureCount: 0,
    lastFailureTime: null,
    circuitOpen: false,
    circuitOpenTime: null,
  };

  // Configuration constants
  private readonly MAX_FAILURES = 3; // Open circuit after 3 consecutive failures
  private readonly CIRCUIT_RESET_MS = 60 * 1000; // Reset circuit after 1 minute
  private readonly INITIAL_BACKOFF_MS = 1000; // Start with 1 second
  private readonly MAX_BACKOFF_MS = 30 * 1000; // Cap at 30 seconds
  private readonly BACKOFF_MULTIPLIER = 2;

  /**
   * Check if a retry should be allowed
   * Returns true if retry is safe, false if circuit is open or too many failures
   */
  shouldRetry(): boolean {
    const now = Date.now();

    // If circuit is open, check if enough time has passed to reset
    if (this.state.circuitOpen && this.state.circuitOpenTime) {
      const timeSinceOpen = now - this.state.circuitOpenTime;
      if (timeSinceOpen >= this.CIRCUIT_RESET_MS) {
        // Reset circuit - allow one attempt
        this.state.circuitOpen = false;
        this.state.circuitOpenTime = null;
        this.state.failureCount = 0;
        if (import.meta.env.DEV) {
          console.log('[Token Retry Guard] Circuit reset - allowing retry');
        }
        return true;
      }
      // Circuit still open
      if (import.meta.env.DEV) {
        console.warn('[Token Retry Guard] Circuit open - blocking retry');
      }
      return false;
    }

    // Circuit is closed - allow retry
    return true;
  }

  /**
   * Record a successful token refresh
   * Resets failure count and closes circuit
   */
  recordSuccess(): void {
    this.state.failureCount = 0;
    this.state.lastFailureTime = null;
    this.state.circuitOpen = false;
    this.state.circuitOpenTime = null;
    if (import.meta.env.DEV) {
      console.log('[Token Retry Guard] Success recorded - resetting state');
    }
  }

  /**
   * Record a failed token refresh attempt
   * Increments failure count and opens circuit if threshold reached
   */
  recordFailure(): void {
    const now = Date.now();
    this.state.failureCount += 1;
    this.state.lastFailureTime = now;

    if (this.state.failureCount >= this.MAX_FAILURES) {
      this.state.circuitOpen = true;
      this.state.circuitOpenTime = now;
      console.error(
        `[Token Retry Guard] Circuit opened after ${this.state.failureCount} failures. ` +
        `Will reset after ${this.CIRCUIT_RESET_MS / 1000} seconds.`
      );
    } else {
      if (import.meta.env.DEV) {
        console.warn(
          `[Token Retry Guard] Failure recorded (${this.state.failureCount}/${this.MAX_FAILURES})`
        );
      }
    }
  }

  /**
   * Calculate exponential backoff delay for next retry
   * Returns delay in milliseconds
   */
  getBackoffDelay(): number {
    if (this.state.failureCount === 0) {
      return 0; // No delay on first attempt
    }

    const delay = Math.min(
      this.INITIAL_BACKOFF_MS * Math.pow(this.BACKOFF_MULTIPLIER, this.state.failureCount - 1),
      this.MAX_BACKOFF_MS
    );

    return delay;
  }

  /**
   * Get current retry state (for debugging/monitoring)
   */
  getState(): Readonly<RetryState> {
    return { ...this.state };
  }

  /**
   * Reset the guard state (useful for testing or manual recovery)
   */
  reset(): void {
    this.state = {
      failureCount: 0,
      lastFailureTime: null,
      circuitOpen: false,
      circuitOpenTime: null,
    };
    if (import.meta.env.DEV) {
      console.log('[Token Retry Guard] State reset');
    }
  }

  /**
   * Check if circuit is currently open
   */
  isCircuitOpen(): boolean {
    return this.state.circuitOpen;
  }
}

// Singleton instance
const tokenRetryGuard = new TokenRetryGuard();

export { tokenRetryGuard };
export type { RetryState };

